// Live.tsx — M0 council theater (rewritten Wave Phase 2 for FE↔BE consistency).
//
// Per docs/page_state_orchestrator/PAGE_STATE_M0.md: consumes new
// SessionStore + useSessionSSE + 4 council molecules (PersonaCard /
// PhaseStrip / IronKillChip / DepthLockBanner) + m0_sessions API.
//
// Replaces W0-era Live.tsx (455 LOC) which subscribed to legacy SSE events
// (phase_change / agent_status / speech / resolved). New backend emits 8
// types per state_machines/session.yaml — incompatible.
//
// PR-PSO-02: 4 substates loading / empty / error / success.
// PR-UX-10: 30-min lock banner; no cancel CTA on this screen (RULE 3 commit).
import { useEffect, useRef, useState } from 'react';
import { useParams, Navigate, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/ui/organisms/PageHeader';
import { sessionGetDetail, sessionGetTranscript } from '@/api/m0_sessions';
import { ApiError } from '@/api/client';
import LoadingState from '@/ui/molecules/LoadingState';
import ErrorState from '@/ui/molecules/ErrorState';
import IntentBadge from '@/components/IntentBadge';
import AbortedSessionBanner from '@/components/AbortedSessionBanner';
import FallbackUsedBanner from '@/components/FallbackUsedBanner';
import AdvanceButton from '@/components/AdvanceButton';
import AttachmentBriefingPanel from '@/components/AttachmentBriefingPanel';
import ToneProfileDisplay from '@/components/ToneProfileDisplay';
import ChoreographyPhaseStrip from '@/ui/molecules/ChoreographyPhaseStrip';
import { PersonaCard, PersonaGrid } from '@/ui/molecules/PersonaCard';
import ChatStream from '@/ui/molecules/ChatStream';
import { IronKillChip, IronKillStack } from '@/ui/molecules/IronKillChip';
import { IronSilentBanner } from '@/ui/molecules/IronSilentBanner';
import { useSessionSSE } from '@/hooks/useSessionSSE';
import {
  useSessionStore,
  selectIsRunning,
  selectIsTerminal,
  ALL_PERSONAS,
  type PersonaId,
} from '@/store/session';
import { useUIStore } from '@/store/ui';
import { useFydUser } from '@/store/auth';
import { sessionAdvance } from '@/api/m0_sessions';

interface LiveLocationState {
  sseUrl?: string;
}

// shouldAutoRedirectToReport — pure predicate for the 4 auto-redirect sites in
// Live. FYD-2026-0512-review-process-link-redirect-loop: when the user lands on
// Live with `?review=1` (clicked the "查看议会过程" Link from /report), we
// MUST suppress the auto-redirect that normally bounces 'completed' status
// back to /report — otherwise the user experiences an instant page reload and
// never gets to see the deliberation timeline.
//
// 'aborted' / 'running' / 'starting' never auto-redirect; only 'completed'
// + non-review combination does.
export function shouldAutoRedirectToReport(status: string | undefined, reviewMode: boolean): boolean {
  if (reviewMode) return false;
  return status === 'completed';
}

export default function Live() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const reviewMode = searchParams.get('review') === '1';
  const { t } = useTranslation();
  const initialSseUrl = (location.state as LiveLocationState | null)?.sseUrl;
  const [recoveredSseUrl, setRecoveredSseUrl] = useState<string | null>(null);
  const [recoveryError, setRecoveryError] = useState<string | null>(null);
  // iter_3: capture IntentVector from sessionGetDetail so IntentBadge can render
  // V-axis (议会方向 converge / diverge / etc) on the Live header.
  const [intent, setIntent] = useState<import('@/types/intent').IntentVector | null>(null);
  // iter_5: capture aborted state separately — replaces generic "disconnected"
  // screen with abort-aware AbortedSessionBanner (P2 empty_state error_aborted).
  const [abortedDetail, setAbortedDetail] = useState<{ reason: string | null } | null>(null);
  // iter_7 (Bug B fix): transcript fetch state.
  // 'not_fetched' → 'loading' → ('merged' | 'transcript_404' | 'transcript_5xx').
  // SSE subscribe is gated on transcriptStatus !== 'loading'.
  type TranscriptStatus = 'not_fetched' | 'loading' | 'merged' | 'transcript_404' | 'transcript_5xx';
  const [transcriptStatus, setTranscriptStatus] = useState<TranscriptStatus>('not_fetched');
  // iter_11 fix: ref-guard which session id has had a transcript fetch
  // started, so re-runs of the effect don't cancel the in-flight request.
  // Was: transcriptStatus in deps array → setTranscriptStatus('loading')
  // triggered cleanup → cancelled=true → fetch resolved silently → stuck.
  const transcriptStartedFor = useRef<string | null>(null);
  const sseUrl = initialSseUrl ?? recoveredSseUrl ?? undefined;

  const current = useSessionStore((s) => s.current);
  const initSession = useSessionStore((s) => s.initSession);
  const mergeTranscript = useSessionStore((s) => s.mergeTranscript);
  const isRunning = useSessionStore(selectIsRunning);
  const isTerminal = useSessionStore(selectIsTerminal);
  const pushModal = useUIStore((s) => s.pushModal);
  const clearModals = useUIStore((s) => s.clearModals);
  const me = useFydUser((s) => s.me);
  // P3: a kill is "first seen in session" when it's the first chip rendered
  // AND the user has never run a council (councils_run==0). Parent owns the
  // detection per ADDENDUM correction 3.
  const userIsFirstTimer = !me || me.councils_run === 0;

  // Recovery flow: arrived without sseUrl (refresh / history click / new tab).
  // Ask BE for a fresh stream_token. Routes terminal sessions to report.
  useEffect(() => {
    if (!id || initialSseUrl || recoveredSseUrl) return;
    let cancelled = false;
    sessionGetDetail(id)
      .then((d) => {
        if (cancelled) return;
        const detail = d as {
          status?: string;
          sse_url?: string | null;
          intent_vector?: import('@/types/intent').IntentVector;
        };
        // iter_3: capture intent_vector regardless of status branch.
        if (detail.intent_vector?.V) setIntent(detail.intent_vector);
        if (shouldAutoRedirectToReport(detail.status, reviewMode)) {
          navigate(`/sessions/${id}/report`, { replace: true });
          return;
        }
        if (detail.status === 'aborted') {
          // iter_5: capture abort_reason for AbortedSessionBanner (P2 spec
          // error_aborted category). Don't fall through to generic disconnect.
          const reason = (d as { abort_reason?: string | null }).abort_reason ?? null;
          setAbortedDetail({ reason });
          return;
        }
        if (detail.sse_url) {
          setRecoveredSseUrl(detail.sse_url);
        } else if (reviewMode) {
          // FYD-2026-0512 review-mode: completed sessions have no live
          // sse_url. That's expected — transcript merge effect below
          // renders history. Do NOT surface "议会会话已断开" error.
        } else {
          setRecoveryError(t('live.disconnectedHeading'));
        }
      })
      .catch((err: Error) => {
        if (!cancelled) setRecoveryError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [id, initialSseUrl, recoveredSseUrl, navigate, t]);

  // Bootstrap: ensure store has session_id; if landing without sse_url, surface fix-up.
  // FYD-2026-0512 review-mode: completed sessions have no live SSE — init the
  // store with an empty sse_url so the transcript merge effect below can run
  // (which gates on current.session_id === id, not on sseUrl).
  useEffect(() => {
    if (!id) return;
    if (current.session_id !== id) {
      if (sseUrl) initSession(id, sseUrl);
      else if (reviewMode) initSession(id, '');
    }
  }, [id, sseUrl, reviewMode, current.session_id, initSession]);

  // iter_7 Bug B fix: fetch persisted transcript BEFORE SSE subscribes so a
  // /live reload mid-council repaints persona bubbles instead of empty cards.
  // Idempotent: runs once per session_id; mergeTranscript skips already-applied
  // events. SSE then resumes from last_event_id (existing useSessionSSE logic).
  useEffect(() => {
    if (!id) return;
    if (transcriptStartedFor.current === id) return; // already started for this session
    if (current.session_id !== id) return; // wait for bootstrap
    transcriptStartedFor.current = id;
    setTranscriptStatus('loading');
    sessionGetTranscript(id)
      .then((tx) => {
        // No `cancelled` guard: this fetch is uniquely keyed by session id
        // via the ref. If the session id changes, navigation unmounts Live.
        mergeTranscript(tx.events ?? []);
        if (tx.ended && tx.abort) {
          setAbortedDetail({ reason: tx.abort.reason });
          setTranscriptStatus('merged');
          return;
        }
        if (tx.ended) {
          if (!reviewMode) {
            navigate(`/sessions/${id}/report`, { replace: true });
          }
          setTranscriptStatus('merged');
          return;
        }
        setTranscriptStatus('merged');
      })
      .catch((err: Error) => {
        const apiErr = err instanceof ApiError ? err : null;
        if (apiErr && apiErr.status === 404) {
          setTranscriptStatus('transcript_404');
          return;
        }
        setTranscriptStatus('transcript_5xx');
      });
  }, [id, current.session_id, mergeTranscript, navigate]);

  // Subscribe to SSE. Only when transcript fetch is no longer pending AND we
  // have id+sseUrl. Gating on transcriptStatus prevents racing the merge with
  // live SSE events (PR-PSO-04 single source of truth).
  // FYD-2026-0512: in review mode (?review=1) the council has already finished
  // and SSE tokens are stale/410. Skip the subscription entirely so we render
  // purely from the merged transcript.
  const sseGate = !reviewMode && id && sseUrl && transcriptStatus !== 'loading' && transcriptStatus !== 'not_fetched';
  const sseStatus = useSessionSSE(
    sseGate
      ? {
          sessionId: id,
          sseUrl,
          onComplete: () => {
            if (!reviewMode) navigate(`/sessions/${id}/report`, { replace: true });
          },
        }
      : { sessionId: id ?? '', sseUrl: '' },
  );

  // Auto-navigate to report when terminal (covers race where useSessionSSE
  // already fired onComplete but mount/unmount missed it). Suppressed in
  // review mode so the user can actually stay on Live and read history.
  useEffect(() => {
    if (reviewMode) return;
    if (isTerminal && current.status === 'completed' && id) {
      const t = window.setTimeout(
        () => navigate(`/sessions/${id}/report`, { replace: true }),
        1200,
      );
      return () => window.clearTimeout(t);
    }
  }, [reviewMode, isTerminal, current.status, id, navigate]);

  useEffect(() => {
    const code = current.error?.code;
    if (code === 'payment_required') {
      pushModal('payment_modal');
    } else if (code === 'wallet_not_connected') {
      pushModal('wallet_connect_modal');
    } else if (code === 'usdt_timeout' || code === 'usdt_wrong_amount' || code === 'usdt_wrong_network') {
      pushModal('payment_failed_error_modal');
    }
  }, [current.error?.code, pushModal]);

  if (!id) return <Navigate to="/dashboard" replace />;

  // iter_5: aborted session detected via sessionGetDetail. Render abort-aware
  // banner (P2 empty_state error_aborted category) instead of generic
  // "无法重连此议会" screen. Solves the iter_5 二死路 UX bug.
  if (abortedDetail) {
    return (
      <div className="max-w-page mx-auto px-8 py-12" data-testid="live-aborted">
        <PageHeader eyebrow={t('live.eyebrow')} title={t('empty.aborted.eyebrow')} />
        <AbortedSessionBanner abortReason={abortedDetail.reason} sessionId={id} />
      </div>
    );
  }

  // iter_7 Bug B: transcript 404 means session evicted from registry. Render
  // the same calming AbortedSessionBanner with a synthetic abortReason so the
  // user sees one explanation, not a hard error.
  if (transcriptStatus === 'transcript_404') {
    return (
      <div className="max-w-page mx-auto px-8 py-12" data-testid="live-transcript-not-available">
        <PageHeader eyebrow={t('live.eyebrow')} title={t('empty.aborted.eyebrow')} />
        <AbortedSessionBanner abortReason="transcript_not_available" sessionId={id} />
      </div>
    );
  }

  // iter_7 Bug B: while transcript fetch is in flight, show a quiet loading
  // state. Pre-iter_7 behavior was to immediately render empty PersonaGrid
  // while SSE connected — looked broken on reload of a long council.
  if (transcriptStatus === 'loading') {
    return (
      <div className="max-w-page mx-auto px-8 py-12" data-testid="live-transcript-loading">
        <PageHeader eyebrow={t('live.eyebrow')} title={t('live.startingTitle')} />
        <LoadingState label={t('live.transcript_loading')} />
      </div>
    );
  }

  // Loading: store has session but no sseUrl yet (deep-link or refresh).
  // Recovery is in flight — show a quiet loading state instead of the
  // alarming "disconnected" screen.
  //
  // FYD-2026-0512 review-mode: completed sessions intentionally have no
  // sse_url. Don't gate the render on sseUrl when in review mode — let the
  // page render straight from transcript merge.
  if (!sseUrl && !recoveryError && !reviewMode) {
    return (
      <div className="max-w-page mx-auto px-8 py-12" data-testid="live-recovering">
        <PageHeader eyebrow={t('live.eyebrow')} title={t('live.startingTitle')} />
        <LoadingState label={t('live.loading')} />
      </div>
    );
  }
  if (!sseUrl && !reviewMode) {
    return (
      <div className="max-w-page mx-auto px-8 py-12" data-testid="live-no-sse">
        <PageHeader eyebrow={t('live.eyebrow')} title={t('live.disconnectedTitle')} />
        <ErrorState
          title={t('live.disconnectedHeading')}
          message={recoveryError ?? t('live.disconnectedBody')}
          retry={() => navigate('/dashboard')}
          retryLabel={t('common.back')}
        />
      </div>
    );
  }

  // Error envelope from SSE.
  if (current.error) {
    // iter_7 Bug C: when BE emits a malformed envelope (no code), FE store
    // now sets code='malformed_sse_event'. Surface a real i18n message
    // instead of "code=unknown".
    const isMalformed = current.error.code === 'malformed_sse_event';
    const title = isMalformed
      ? t('live.malformed_event')
      : (current.error.message?.default_text_zh ?? current.error.code);
    const traceId = current.error.trace_id || '—';
    return (
      <div className="max-w-page mx-auto px-8 py-12" data-testid="live-error">
        <PageHeader eyebrow={t('live.eyebrow')} title={t('live.errorHeading')} />
        <ErrorState
          title={title}
          message={`code=${current.error.code} · trace_id=${traceId}`}
          retry={() => navigate('/dashboard')}
          retryLabel={t('common.back')}
        />
      </div>
    );
  }

  const v9HasWarning = current.v9_status === 'warning';
  // FYD-2026-0512 review-mode: store.status starts as 'init' after the empty
  // initSession() call and may not flip to 'completed' until the close event
  // is merged from transcript. Force the terminal title in review mode so
  // the user doesn't see "议会启动中" on a finished council.
  const liveTitle = reviewMode
    ? t('live.terminalTitle')
    : isRunning
      ? t('live.runningTitle')
      : isTerminal
        ? t('live.terminalTitle')
        : t('live.startingTitle');

  // ★ M2 (handoff.md row 7): persona render order derived from choreography
  // speakers (deduped). Within current phase, sort by spoken-order
  // (monotonic_id_first) so user sees who spoke 1st, 2nd, 3rd as soon as
  // events arrive — earlier P0 bug surfaced by user "发言看不到顺序".
  // Speakers who have NOT spoken yet stay at the end in canonical order.
  // Cross-phase: each phase resets monotonic_id_first (store reducer in
  // phase_started); historical speakers (prior phases) sort to end of "spoken"
  // group only if they speak again — fine, and matches spec §3.X PersonaCard
  // intent.
  const speakerSet = new Set<PersonaId>();
  current.choreography?.phases.forEach((p) =>
    p.speakers.forEach((s) => speakerSet.add(s as PersonaId)),
  );
  const choreoSpeakers = ALL_PERSONAS.filter((r) => speakerSet.has(r));
  const fallbackSpeakers: PersonaId[] = ['sage', 'hammer', 'compass', 'horizon', 'iron'];
  const speakerPool: PersonaId[] = choreoSpeakers.length > 0 ? choreoSpeakers : fallbackSpeakers;
  // Current-phase queue rank: 1-based index in phases[idx].speakers[] · gives
  // every queued speaker a number BEFORE they speak (用户反馈 "发言还是缺失顺序").
  // Cross-phase: speakers outside current phase get rank 0 (no badge).
  const currentPhaseSpeakers: readonly PersonaId[] =
    current.current_phase_idx != null && current.choreography?.phases
      ? ((current.choreography.phases[current.current_phase_idx]?.speakers ?? []) as PersonaId[])
      : [];
  const queueRank = new Map<PersonaId, number>();
  currentPhaseSpeakers.forEach((pid, idx) => {
    if (!queueRank.has(pid)) queueRank.set(pid, idx + 1);
  });

  // Stable sort: queued speakers in queue order first · then non-queue speakers.
  // Within queued: if both spoken, earlier-spoken first; else queue-order.
  const renderOrder: PersonaId[] = [...speakerPool].sort((a, b) => {
    const aq = queueRank.get(a) ?? 0;
    const bq = queueRank.get(b) ?? 0;
    if (aq === 0 && bq === 0) return 0;
    if (aq === 0) return 1;                       // a not queued · push back
    if (bq === 0) return -1;
    // Both queued · prefer spoken-first ordering if both spoken
    const af = current.personas[a]?.monotonic_id_first ?? 0;
    const bf = current.personas[b]?.monotonic_id_first ?? 0;
    if (af > 0 && bf > 0) return af - bf;
    return aq - bq;                               // fallback to queue index
  });
  // speakerRank exposes queue position (1-based) so PersonaCard shows badge
  // for all queued speakers regardless of spoken status.
  const speakerRank = queueRank;

  // ★ M2 (handoff.md row 3 + row 8): AdvanceButton phase_summary lookup —
  // when paused at idx, pull summary by the choreography phase name.
  const pausedPhaseName =
    current.current_phase_idx != null && current.choreography?.phases
      ? current.choreography.phases[current.current_phase_idx]?.name
      : typeof current.current_phase === 'string'
        ? current.current_phase
        : null;
  const pausedPhaseSummary = pausedPhaseName
    ? (current.phase_summaries[pausedPhaseName] ?? '')
    : '';

  const onAdvance = async () => {
    if (!id || current.current_phase_idx == null) return;
    try {
      // ★ pass-5 fix C14: prefer phase_paused-emitted advance_endpoint when present.
      await sessionAdvance(id, current.current_phase_idx, {
        endpointOverride: current.advance_endpoint ?? undefined,
      });
    } catch (err) {
      // P0-10 epoch errors / 410 terminal — leave UI for parent to react via
      // SSE phase_advanced (or session_terminated → navigate Report).
      console.warn('sessionAdvance failed', err);
    }
  };

  return (
    <div className="max-w-page mx-auto px-8 py-12" data-testid="live-root">
      <PageHeader
        eyebrow={
          reviewMode
            ? t('live.reviewMode', { defaultValue: '回看 · 议会已结束' })
            : `${t('live.eyebrow')} · ${sseStatus.status}`
        }
        title={liveTitle}
      />

      {id && (
        <button
          type="button"
          onClick={() => {
            if (navigator?.clipboard) {
              void navigator.clipboard.writeText(id);
            }
          }}
          className="inline-flex items-center gap-2 mb-4 px-3 py-1 border border-hairline rounded font-mono text-[11px] tracking-meta text-ink-3 hover:text-ink hover:border-ink/40 transition"
          title={t('live.copySessionId')}
          data-testid="session-id-chip"
        >
          <span className="text-ink-2 uppercase">session_id</span>
          <span className="text-ink-2">·</span>
          <span className="text-ink">{id}</span>
        </button>
      )}

      {/* ★ M2 §9.2 P0-12: fallback_used banner (single source of truth = store). */}
      {current.fallback_used && (
        <FallbackUsedBanner reason={current.fallback_reason ?? null} />
      )}

      {/* ★ M2 §6: Sage attachment briefing (FIX-C4 invariant — only render
          when briefing.available === true). */}
      {current.attachment_briefing?.available && (
        <AttachmentBriefingPanel briefing={current.attachment_briefing} />
      )}

      {v9HasWarning && (
        <IronSilentBanner
          onDismiss={clearModals}
          onRetry={() => navigate('/dashboard')}
        />
      )}

      {/* iter_3: V-axis visibility — show user which 议会方向 their council
          is running (converge / diverge / diagnose / resolve / validate / project).
          Hidden gracefully when intent is null (legacy non-vector sessions).
          ★ M2 §7: scene_symbols primary 与 V 轴并列显. */}
      <div className="flex items-center justify-between gap-4 mb-3">
        {/* ★ M2 (FE_SPEC §3.X.3.12): choreography-derived phase strip molecule —
            replaces M0 6-phase strip + the previously-inlined JSX. */}
        <ChoreographyPhaseStrip
          phases={current.choreography?.phases ?? []}
          current_phase_idx={
            current.current_phase_idx != null
              ? current.current_phase_idx
              : (() => {
                  const idx = current.choreography?.phases?.findIndex(
                    (p) => p.name === current.current_phase,
                  );
                  return idx != null && idx >= 0 ? idx : null;
                })()
          }
          phases_completed={current.phases_completed}
          phases_total={current.phases_total}
          loading={!current.choreography}
        />
        <IntentBadge
          intent={current.intent_inferred?.intent_vector ?? current.intent_vector ?? intent}
          scene_symbols={current.scene_symbols.length > 0 ? current.scene_symbols : undefined}
        />
      </div>

      {/* ★ M2 (FE_SPEC §3.X.3.11): tone profile display when Composer set one. */}
      {current.choreography?.tone_profile && (
        <div className="mb-4">
          <ToneProfileDisplay tone_profile={current.choreography.tone_profile} compact />
        </div>
      )}

      {/* ★ M2 (FE_SPEC §3.X.3.6): aborted banner when SSE flipped status. */}
      {current.status === 'aborted' && (
        <AbortedSessionBanner
          reason={current.abort_reason ?? 'unknown'}
          sessionId={id}
          canViewReport
        />
      )}

      {/* ★ M2.1 (用户反馈 "议会改聊天对话线性形式"): ChatStream 替代 PersonaGrid.
        Linear chronological flow · auto-scroll on new chunks · phase markers
        + iron_kill inline alerts.
        FYD-2026-0512 review-mode: skip the PersonaGrid fallback entirely so
        users reviewing a finished council never see the '等待发言…' empty
        cards — ChatStream handles its own empty state.  */}
      {current.messages.length > 0 || reviewMode ? (
        <ChatStream messages={current.messages} liveMode={isRunning} />
      ) : (
        <PersonaGrid>
          {renderOrder.map((pid) => {
            const buf = current.personas[pid];
            const speech = buf.chunks.join('');
            const rank = speakerRank.get(pid) ?? 0;
            if (pid === 'iron' && current.iron_kills.length > 0) {
              return (
                <PersonaCard
                  key={pid}
                  personaId={pid}
                  speechBody={speech}
                  isFinal={buf.complete}
                  monotonicIdLast={buf.monotonic_id_last}
                  spokenRank={rank}
                >
                  <IronKillStack>
                    {current.iron_kills.map((k, idx) => (
                      <IronKillChip
                        key={k.monotonic_id}
                        candidateId={k.candidate_id}
                        killReason={k.reason}
                        monotonicId={k.monotonic_id}
                        isFirstSeenInSession={idx === 0 && userIsFirstTimer}
                      />
                    ))}
                  </IronKillStack>
                </PersonaCard>
              );
            }
            return (
              <PersonaCard
                key={pid}
                personaId={pid}
                speechBody={speech}
                isFinal={buf.complete}
                monotonicIdLast={buf.monotonic_id_last}
                spokenRank={rank}
              />
            );
          })}
        </PersonaGrid>
      )}

      {sseStatus.status === 'connecting' && current.last_event_id === 0 && (
        <LoadingState variant="skeleton" rows={2} label="等待 P1 framing 启动..." />
      )}

      {/* Sage synthesis (P6): same component, different persona id.
          Suppressed in review mode — ChatStream already contains the
          sage_synthesis speech in chronological order. */}
      {!reviewMode && current.current_phase === 'resolving' && (
        <div className="mt-6">
          <PersonaCard
            personaId="sage_synthesis"
            speechBody={current.personas.sage_synthesis.chunks.join('')}
            isFinal={current.personas.sage_synthesis.complete}
            monotonicIdLast={current.personas.sage_synthesis.monotonic_id_last}
          />
        </div>
      )}

      {/* ★ M2 (handoff.md row 2/3 + PRD §7.9): AdvanceButton mount under
          GatingMode=user_advance · awaiting_advance=true · current_phase_idx
          set. SSE phase_advanced flips awaiting_advance=false → unmount.
          ★ pass-5 fix: respects can_advance gate from phase_paused payload. */}
      {(current.choreography?.gating_mode === 'user_advance' ||
        current.gating_mode === 'user_advance' ||
        current.awaiting_advance) &&
        current.awaiting_advance &&
        current.current_phase_idx != null && pausedPhaseName && (
          <AdvanceButton
            phase_name={pausedPhaseName}
            phase_summary={pausedPhaseSummary}
            on_advance={onAdvance}
            disabled={!current.can_advance}
          />
        )}
    </div>
  );
}
